import { memo, useMemo } from 'react';
import { Pressable } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSequence, withSpring, withTiming } from 'react-native-reanimated';
import { Text, YStack } from 'tamagui';
import { useThemeColors, Icon } from '@kiips/ui';

interface NoteMarkdownProps {
  /** Raw notes markdown — line-separated. */
  content: string;
  /** Fired when a checkbox line at `lineIndex` is tapped. Optional —
   *  when omitted the renderer is read-only. */
  onToggle?: ((lineIndex: number) => void) | undefined;
  /** Currently-checked overrides keyed by line index. Used by callers
   *  that want to maintain local toggle state without rewriting the
   *  raw `content` string. */
  toggleOverrides?: Record<number, boolean> | undefined;
}

const HASHTAG_RE = /(#[A-Za-z][\w-]*)/g;

/**
 * Renders notes markdown in the design language:
 *  - `- [ ]` / `- [x]` lines become tap-to-toggle checkbox rows with a
 *    small spring animation when toggled.
 *  - `#hashtag` runs render as inline dark pills.
 *  - Plain text otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
export const NoteMarkdown = memo(function NoteMarkdown({ content, onToggle, toggleOverrides }: NoteMarkdownProps) {
  const lines = useMemo(() => content.split('\n'), [content]);
  // Markdown lines render in source order; the index IS the stable
  // identifier (duplicate lines are common — two blanks, two "- item"
  // rows). Toggling re-renders by content because `lines` is memoized
  // on `content`.
  return (
    <YStack gap={6}>
      {lines.map((line, i) => {
        const cb = parseCheckbox(line);
        if (cb) {
          const checked = toggleOverrides?.[i] ?? cb.checked;
          return (
            <CheckboxRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              text={cb.text}
              checked={checked}
              onPress={onToggle ? () => onToggle(i) : undefined}
            />
          );
        }
        // eslint-disable-next-line react/no-array-index-key
        if (line.trim().length === 0) return <YStack key={i} height={8} />;
        // eslint-disable-next-line react/no-array-index-key
        return <NoteLine key={i} text={line} />;
      })}
    </YStack>
  );
});

function parseCheckbox(line: string): { checked: boolean; text: string } | null {
  const m = line.match(/^\s*- \[( |x|X)\] (.*)$/);
  if (!m) return null;
  return { checked: m[1]!.toLowerCase() === 'x', text: m[2]! };
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
const CheckboxRow = memo(function CheckboxRow({ text, checked, onPress }: { text: string; checked: boolean; onPress?: (() => void) | undefined}) {
  const theme = useThemeColors();
  const scale = useSharedValue(1);
  const animStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }));

  const handlePress = () => {
    if (!onPress) return;
    scale.value = withSequence(withTiming(0.85, { duration: 80 }), withSpring(1, { damping: 8 }));
    onPress();
  };

  return (
    <Pressable
      onPress={handlePress}
      disabled={!onPress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={text}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 4 }}
    >
      <Animated.View
        style={[
          {
            width: 22,
            height: 22,
            borderRadius: 6,
            borderWidth: 1.5,
            borderColor: theme.textOnSurface,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: checked ? theme.textOnSurface : theme.surface,
          },
          animStyle,
        ]}
      >
        {checked ? <Icon name="check" size={14} color={theme.textInverse} /> : null}
      </Animated.View>
      <Text
        flex={1}
        fontSize={14}
        color={checked ? '$textSubdued' : '$textOnSurface'}
        textDecorationLine={checked ? 'line-through' : 'none'}
      >
        {text}
      </Text>
    </Pressable>
  );
});

// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
const NoteLine = memo(function NoteLine({ text }: { text: string }) {
  const parts = text.split(HASHTAG_RE);
  return (
    <Text fontSize={14} color="$textOnSurface" lineHeight={22}>
      {parts.map((part, i) => {
        if (/^#[A-Za-z][\w-]*$/.test(part)) {
          return (
            <Text
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              backgroundColor="$textOnSurface"
              color="$textInverse"
              fontSize={12}
              fontWeight="600"
              paddingHorizontal={8}
              paddingVertical={2}
              borderRadius={10}
            >
              {part}
            </Text>
          );
        }
        // eslint-disable-next-line react/no-array-index-key
        return <Text key={i}>{part}</Text>;
      })}
    </Text>
  );
});
